import authService from '@jobseeker/common-core/services/auth.service';
import {storeAuth, getAuth} from '@jobseeker/common-core/utils/authHelper';

import type {
  AuthType,
  RegisterType,
} from '@jobseeker/common-core/services/types';
import type {Dispatch} from 'react';
import type {AnyAction} from 'redux';
import {getAllParamsFromUrl} from '@jobseeker/common-core/utils/common';

const login = async (
  email: string,
  password: string,
  dispatch: Dispatch<AnyAction>,
  grecaptcha?: string
): Promise<AuthType> => {
  const auth: AuthType = await authService.login('login', {
    email,
    password,
    recaptchaToken: grecaptcha,
  });
  if (auth['twoFactorAuth'] || auth.options?.reset) {
    return auth;
  }

  if (auth['token']) {
    dispatch({type: 'Auth/set_user_loggedin'});
    dispatch({type: 'Auth/set_auth', payload: getAuth()});
    dispatch({
      type: 'Auth/set_user_info',
      payload: {
        userId: auth.userId,
        obfuscatedUserId: auth.obfuscatedUserId,
      },
    });
    return {
      token: auth.token,
      userId: auth.userId,
    };
  } else if (auth['activationToken']) {
    return {
      message: auth.message,
      activationToken: auth['activationToken'],
    };
  } else {
    throw auth['error'];
  }
};

const sendLoginOtp = async (
  email: string,
  source: 'login' | 'register' = 'login'
) => {
  const res = await authService.sendOtp('auth/emailOtpSend', {email, source});
  if (res?.success) {
    return {success: true, name: res?.name};
  } else {
    throw res['error'];
  }
};

const verifyLoginOtp = async (
  email: string,
  otp: string,
  dispatch: Dispatch<AnyAction>,
  source: 'login' | 'register' = 'login',
  phone?: string
): Promise<AuthType> => {
  const auth: AuthType = await authService.verifyOtp('auth/emailOtpVerify', {
    email,
    otp,
    source,
    refPool: JSON.stringify(getAllParamsFromUrl()),
    ...(phone ? {phone} : {}),
  });
  if (auth['token']) {
    dispatch({type: 'Auth/set_user_loggedin'});
    dispatch({type: 'Auth/set_auth', payload: getAuth()});
    dispatch({
      type: 'Auth/set_user_info',
      payload: {
        userId: auth.userId,
        obfuscatedUserId: auth.obfuscatedUserId,
      },
    });
    return auth;
  } else {
    throw auth['error'];
  }
};

const sendMobileLoginOtp = async (
  phone: string,
  source: 'login' | 'register' = 'login'
) => {
  const res = await authService.sendMobileOtp('auth/mobileOtpSend', {
    phone,
    source,
  });
  if (res?.success) {
    return {success: true, name: res?.name};
  } else {
    throw res['error'];
  }
};

const verifyMobileLoginOtp = async (
  phone: string,
  otp: string,
  dispatch: Dispatch<AnyAction>,
  source: 'login' | 'register' = 'login',
  email?: string
): Promise<AuthType> => {
  const auth: AuthType = await authService.verifyMobileOtp(
    'auth/mobileOtpVerify',
    {
      phone,
      otp,
      source,
      refPool: JSON.stringify(getAllParamsFromUrl()),
      ...(email ? {email} : {}),
    }
  );
  if (auth['token']) {
    dispatch({type: 'Auth/set_user_loggedin'});
    dispatch({type: 'Auth/set_auth', payload: getAuth()});
    dispatch({
      type: 'Auth/set_user_info',
      payload: {
        userId: auth.userId,
        obfuscatedUserId: auth.obfuscatedUserId,
      },
    });
    return auth;
  } else {
    throw auth['error'];
  }
};

const isEmailRequiredForRegistrationError = (code?: number): boolean =>
  code === 422;

const isEmailAlreadyExistsError = (code?: number): boolean => code === 409;

const register = async (
  email: string,
  password: string,
  confirmPassword: string,
  policyCheck: boolean,
  dispatch: Dispatch<AnyAction>
) => {
  const refPool = getAllParamsFromUrl();
  const refValue = refPool?.ref ?? '';
  const monthCity =
    (refValue === 'bridge_online' || refValue === 'bridge_permanent') &&
    typeof window !== 'undefined'
      ? JSON.parse(localStorage.getItem('talentBridgeMonthCity') || 'null')
      : null;
  const month = monthCity?.month ?? '';
  const city = monthCity?.city ?? '';
  const payload: any = {
    email,
    password,
    confirmPassword,
    policyCheck,
    refPool: JSON.stringify(refPool),
  };
  if (month) payload.month = month;
  if (city) payload.city = city;
  const auth: RegisterType = await authService.register('register', payload);
  if (auth['token']) {
    storeAuth(auth['token'], auth['expires']);
    dispatch({type: 'Auth/set_user_loggedin'});
    dispatch({type: 'Auth/set_auth', payload: getAuth()});
    dispatch({
      type: 'Auth/set_user_info',
      payload: {id: auth['userId'], emailVerified: auth['emailVerified']},
    });
    return {valid: true, userId: auth['userId']};
  } else {
    throw auth['error'];
  }
};

const forgotPassword = async (email: string, grecaptcha?: string) => {
  const resInfo = await authService.forgotPassword('forgot', {
    email,
    recaptchaToken: grecaptcha,
  });
  if (resInfo?.activationToken) {
    return {
      message: resInfo.message,
      activationToken: resInfo.activationToken,
    };
  }
  if (!resInfo?.error?.message && resInfo['message']) {
    return resInfo['message'];
  } else {
    throw resInfo['error'];
  }
};

const resendPasswordPolicyEmail = async (email: string) => {
  const resInfo = await authService.resendPasswordPolicyEmail('resendemail', {
    email,
  });
  if (!resInfo?.error?.message && resInfo['message']) {
    return resInfo['message'];
  } else {
    throw resInfo['error'];
  }
};

const resetPassword = async (
  password: string,
  confirmPassword: string,
  forConsent?: boolean
): Promise<any> => {
  const urlParams = new URLSearchParams(window.location.search);
  const passkey = urlParams.get(forConsent ? 'activate' : 'passkey');

  const payload: any = {
    password,
    confirmPassword,
    passkey,
    forConsent,
  };

  if (!forConsent) {
    const email = urlParams.get('email');
    payload.email = email;
  }

  const info = await authService.resetPassword('resetpassword', payload);

  if (info?.message) {
    return info.message;
  } else {
    throw info['error'];
  }
};

const isOtpRateLimitError = (message?: string): boolean =>
  /too many (incorrect )?otp attempts/i.test(message || '');

export {
  login,
  register,
  forgotPassword,
  resendPasswordPolicyEmail,
  resetPassword,
  sendLoginOtp,
  verifyLoginOtp,
  sendMobileLoginOtp,
  verifyMobileLoginOtp,
  isEmailRequiredForRegistrationError,
  isEmailAlreadyExistsError,
  isOtpRateLimitError,
};
